'use client'

import { useEffect } from 'react'
import Link from 'next/link'
import { datadogRum } from '@datadog/browser-rum'
import { ContentSection } from 'sidecar-components/atoms/ContentSection'
import { Icon, IconSize } from 'sidecar-components/atoms/Icon'
import { Button, ButtonSize } from 'sidecar-components/atoms/Button'
import { logger } from '@/lib/services/logger.service'

/**
 * Route-level error boundary for the group portal.
 *
 * Catches errors in route segments and nested components, providing a
 * user-friendly fallback UI with recovery options.
 *
 * This boundary is automatically invoked by Next.js when an error occurs within
 * a route segment during rendering, in Server Components, or in Server
 * Actions.
 */

interface ErrorProps {
  /** Error object with optional digest for tracking */
  error: Error & { digest?: string }
  /** Function to attempt recovering by re-rendering the segment */
  reset: () => void
}

export default function Error({ error, reset }: ErrorProps) {
  useEffect(() => {
    logger.error('Route Error', error, {
      source: 'nextjs-route-error',
      digest: error.digest,
    })
    try {
      datadogRum.addError(error, {
        source: 'nextjs-route-error',
        digest: error.digest,
      })
    } catch {
      // RUM may not be initialized; logger.error already captured the event
    }
  }, [error])

  return (
    <ContentSection breathable className="text-center">
      <Icon
        name="sidecar"
        className="mx-auto color-iconBrand-default"
        size={IconSize.XXXXL}
      />

      <h1 className="my-12">
        We encountered an unexpected error. Please try again or return to the
        home page.
      </h1>

      {/* Error digest for support reference (production only) */}
      {error.digest && (
        <p className="text-sm color-text-weak mb-4">Error ID: {error.digest}</p>
      )}

      <div className="flex gap-4 justify-center">
        {/* Try again button - attempts to recover by re-rendering */}
        <Button
          size={ButtonSize.LARGE}
          icon="refresh"
          onClick={reset}
          testId="btn_tryAgain"
        >
          Try again
        </Button>

        {/* Return home button - safe fallback navigation */}
        <Link href="/">
          <Button
            size={ButtonSize.LARGE}
            icon="forward"
            testId="btn_returnHome"
          >
            Return home
          </Button>
        </Link>
      </div>
    </ContentSection>
  )
}
